import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';

import { AppIconComponent } from '../../../../components/icon/icon.component';
import { BroadcastService } from '../../../../services/broadcast.service';
import { LocalMediaPermissionService } from '../../../../services/local-media-permission.service';
import { ZegoEngineService } from '../../../../services/zego-engine.service';
import { ZimService } from '../../../../services/zim.service';
import { createLogger } from '../../../../utils/logger.util';
import { CALL_INCOMING_STATUS, CALL_STATE, CALL_TYPE } from '../../constants';
import { CallStoreService } from '../../services/call-store.service';
import { CallUserResolverService } from '../../services/call-user-resolver.service';
import { PipService } from '../../services/pip.service';

@Component({
  selector: 'app-call-status-bar',
  standalone: true,
  imports: [AppIconComponent],
  templateUrl: './call-status-bar.component.html',
  styleUrls: ['./call-status-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallStatusBarComponent {
  private readonly logger = createLogger('Call - CallStatusBarComponent');
  callStore = inject(CallStoreService);
  private zimService = inject(ZimService);
  private zegoEngineService = inject(ZegoEngineService);
  private callUserInfoResolver = inject(CallUserResolverService);
  private localMediaPermissionService = inject(LocalMediaPermissionService);
  private broadcastService = inject(BroadcastService);
  private pipService = inject(PipService);

  CALL_STATE = CALL_STATE;
  CALL_TYPE = CALL_TYPE;

  readonly currentUserId = this.zimService.userId;
  readonly localMicState = this.callStore.localMicState;
  readonly isMicPermissionGranted = this.callStore.isMicPermissionGranted;
  readonly hasMicDevice = this.callStore.hasMicDevice;
  readonly visible = this.callStore.showCallStatusBar;

  readonly statusColor = computed<'#1570EF' | '#079455' | '#FE9A00' | null>(() => {
    const state = this.callStore.callState();

    if (state === CALL_STATE.CONNECTED && this.isIncomingCall()) {
      return '#FE9A00';
    }

    if (
      state === CALL_STATE.OUTGOING ||
      state === CALL_STATE.RINGING ||
      state === CALL_STATE.CONNECTING ||
      this.isIncomingCall()
    )
      return '#1570EF';

    if (state === CALL_STATE.CONNECTED) return '#079455';

    return null;
  });

  isInMeeting = signal(false);

  isWaiting = computed(
    () => this.callStore.pendingInvitation()?.status === CALL_INCOMING_STATUS.WAITING,
  );

  readonly isIncomingCall = computed(() => {
    return !!this.callStore.pendingInvitation();
  });

  readonly isCallAudio = computed(() => {
    return (
      this.callStore.currentCall()?.type === CALL_TYPE.AUDIO ||
      this.callStore.pendingInvitation()?.type === CALL_TYPE.AUDIO
    );
  });

  readonly userName = this.callUserInfoResolver.callerName;

  readonly label = computed(() => {
    const pending = this.callStore.pendingInvitation();
    if (pending) {
      return `Cuộc gọi ${pending.type === CALL_TYPE.AUDIO ? 'thoại' : 'video'}`;
    }

    const state = this.callStore.callState();
    const call = this.callStore.currentCall();

    switch (state) {
      case CALL_STATE.OUTGOING:
        return 'Đang gọi';
      case CALL_STATE.RINGING:
        return 'Đang đổ chuông';
      case CALL_STATE.CONNECTING:
        return 'Đang nối máy đến người nhận';
      case CALL_STATE.CONNECTED: {
        return call ? `Cuộc gọi ${call.type === CALL_TYPE.AUDIO ? 'thoại' : 'video'}` : 'Cuộc gọi';
      }
      default:
        return '';
    }
  });

  callDuration = this.callStore.callDurationText;

  constructor() {
    effect(() => {
      if (!this.isIncomingCall()) return;
      this.broadcastService.checkIsInMeeting().then((inMeeting) => this.isInMeeting.set(inMeeting));
    });
  }

  showModal() {
    if (this.pipService.isOpen()) return;
    this.callStore.isModalHidden.set(false);
  }

  toggleMic(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    // Unlock audio cho Safari khi user toggle mic
    this.zegoEngineService.unlockAudioFromUserInteraction();
    this.callStore.toggleMic();
  }

  async endCall(event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
    }
    await this.callStore.endCurrentCall();
  }

  async acceptIncomingCall(event: Event): Promise<void> {
    event.stopPropagation();
    const pending = this.callStore.pendingInvitation();
    if (!pending) return;
    const checkPermission =
      await this.localMediaPermissionService.requestCameraMicPermission('mic');
    if (!checkPermission) return;
    try {
      if (this.isInMeeting()) {
        this.broadcastService.meeting({
          action: 'force-leave',
          payload: {},
        });
      }
      await this.callStore.callAccept(pending.callId);
    } catch (error) {
      this.logger.error('Failed to accept call:', error);
    }
  }

  async rejectIncomingCall(event: Event): Promise<void> {
    event.stopPropagation();
    const pending = this.callStore.pendingInvitation();
    if (pending) {
      try {
        await this.callStore.callReject(pending.callId, false);
      } catch (error) {
        this.logger.error('Failed to reject call:', error);
      }
    }
  }
}
